/**
 * WHERE THE DOOR LEFT THE PAW, so the wizard can pick it up from there.
 *
 * Freddi, 2026-09-16: *"there should be a smooth animation from the initial
 * registration page … the paw should smoothly and naturally move up to the top
 * left header"*.
 *
 * THE HONEST PROBLEM FIRST. The door (`/join/:token`) and the wizard are not
 * one page with a transition between them — between the two the browser leaves
 * this origin entirely for Google or Microsoft and comes back. There is no
 * shared DOM, no View Transition, no element that survives the trip. A literal
 * continuous move is not available at any price.
 *
 * What IS available is better than an approximation of it: the door writes down
 * exactly where its paw is sitting, that note survives the round trip in
 * `sessionStorage` (per tab, across navigations, including the cross-origin
 * hop), and the wizard opens with its own paw laid over that spot at that size
 * before flying it to the header. The eye tracks one object across the
 * interruption. It reads as continuity because, in every way the customer can
 * check, it IS continuity.
 *
 * THE NOTE IS ONLY EVER A HINT. Every path through here falls back to a
 * proportional opening position and then to no flight at all — a customer who
 * came back in a different tab, who opened `/register` directly, or whose
 * browser refuses storage gets a page that is already correct, with the mark in
 * its header seat. Nothing here is allowed to be load-bearing.
 */

/** Where the door's mark sat, in viewport coordinates, and in what viewport. */
export interface PawSeat {
  /** Centre of the mark. */
  cx: number;
  cy: number;
  /** Its rendered width — the door's is 56, the header's 28. */
  size: number;
  /** The viewport it was measured in; a different one invalidates the note. */
  vw: number;
  vh: number;
}

const SEAT_KEY = 'bbbk.paw.seat';
const FLOWN_KEY = 'bbbk.paw.flown';

/**
 * Storage throws rather than returns null in a locked-down browser (Safari
 * private mode historically, any browser with site data blocked), and it throws
 * on READ as well as write. Every call goes through here.
 */
function store<T>(run: (s: Storage) => T): T | null {
  try {
    return run(window.sessionStorage);
  } catch {
    return null;
  }
}

/** The door, telling the wizard where to start. Safe to call on every layout. */
export function recordPawSeat(el: Element | null): void {
  if (!el) return;
  const r = el.getBoundingClientRect();
  if (r.width === 0) return; // not laid out yet, or display:none
  const seat: PawSeat = {
    cx: r.left + r.width / 2,
    cy: r.top + r.height / 2,
    size: r.width,
    vw: window.innerWidth,
    vh: window.innerHeight,
  };
  store((s) => s.setItem(SEAT_KEY, JSON.stringify(seat)));
}

/**
 * The seat the door recorded, or null.
 *
 * A NOTE FROM A DIFFERENT VIEWPORT IS NOT A NOTE. Rotate the phone while the
 * provider has the screen and the recorded point now names somewhere else
 * entirely — a paw that flies in from off-screen left is worse than one that
 * simply appears. The tolerance is generous (a 10% change, which absorbs a
 * browser's own chrome collapsing on scroll) and anything past it is discarded.
 */
export function readPawSeat(): PawSeat | null {
  const raw = store((s) => s.getItem(SEAT_KEY));
  if (!raw) return null;
  let seat: unknown;
  try {
    seat = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isSeat(seat)) return null;
  const moved =
    Math.abs(seat.vw - window.innerWidth) / Math.max(1, seat.vw) > 0.1 ||
    Math.abs(seat.vh - window.innerHeight) / Math.max(1, seat.vh) > 0.1;
  return moved ? null : seat;
}

function isSeat(v: unknown): v is PawSeat {
  if (typeof v !== 'object' || v === null) return false;
  const s = v as Record<string, unknown>;
  const nums = ['cx', 'cy', 'size', 'vw', 'vh'];
  return nums.every((k) => typeof s[k] === 'number' && Number.isFinite(s[k] as number)) && (s.size as number) > 0;
}

/**
 * Where to fly from when there is no note — the door's own proportions rather
 * than a guess.
 *
 * The door is a centred column whose mark sits near the top of the card, which
 * on every phone lands the paw around a third of the way down. Being
 * proportional it holds on a tablet too, which a stored pixel value would not.
 */
export function fallbackSeat(): PawSeat {
  return {
    cx: window.innerWidth / 2,
    cy: window.innerHeight * 0.32,
    size: 56,
    vw: window.innerWidth,
    vh: window.innerHeight,
  };
}

/** The transform that puts the header's mark back where the door's was. */
export function invertTo(seat: PawSeat, rest: DOMRect): { dx: number; dy: number; scale: number } {
  return {
    dx: seat.cx - (rest.left + rest.width / 2),
    dy: seat.cy - (rest.top + rest.height / 2),
    scale: rest.width > 0 ? seat.size / rest.width : 1,
  };
}

/**
 * Has the paw already flown in this sitting?
 *
 * ONCE, AND THEN NEVER AGAIN. The flight exists to carry the customer over the
 * gap the provider opened; it has no job on the second step, and a brand mark
 * that re-enacts its arrival every time the page changes is a delay wearing a
 * flourish. Reads and claims in one call so two mounts in the same frame cannot
 * both win it.
 */
export function claimFlight(): boolean {
  const already = store((s) => s.getItem(FLOWN_KEY));
  if (already) return false;
  // Storage refused: fly. A customer who cannot be remembered is better served
  // by the animation running twice than by it never running at all.
  store((s) => s.setItem(FLOWN_KEY, '1'));
  return true;
}

/** For tests, and for a sign-out that should leave nothing of this behind. */
export function resetFlight(): void {
  store((s) => {
    s.removeItem(SEAT_KEY);
    s.removeItem(FLOWN_KEY);
  });
}
